import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Button } from 'src/components/shared/Button'
import { CustomerLogoUploader } from 'src/components/customer/shared/CustomerLogoUploader'
import { Chip } from 'src/components/shared/Chip'
import { CommonModal } from 'src/components/shared/CommonModal'
import { DataRow } from 'src/components/shared/DataRow'
import { AdminLoginAsCustomer, AdminOnboardMasterContractor } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { Customer, LoginAsCustomerResult } from 'src/interfaces/customer'
import {
  isCustomerOnboardedAsMasterContractor,
  resolveCustomerDisplayName,
} from 'src/utils/customerList'
import { buildSignInAsUrl } from 'src/utils/signInAsUrl'
import { semanticColors } from 'src/constants/semanticTokens'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  customer: Customer
  onCustomerChanged(): void
}

/**
 * The customer's identity card on the Overview page, laid out like the dashboard's contractor
 * details panel: the name as a heading, then one `DataRow` per fact separated by hairlines,
 * and the actions as full-width grey buttons under the last rule — the way "Deactivate now"
 * sits at the foot of a contractor's panel.
 */
const CustomerDetailsCardComponent = ({ customer, onCustomerChanged }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  // One permission per endpoint (ENG-1207 part 2): PUT /partners/:id, POST
  // /partners/onboard-master-contractor and POST /partners/:id/login-as.
  const canUploadLogo = useHasPermission(InternalPermission.PartnerSettingsManage)
  const canOnboardMasterContractor = useHasPermission(
    InternalPermission.PartnerOnboardMasterContractor,
  )
  const canLoginAs = useHasPermission(InternalPermission.PartnersLoginAs)

  const [confirmingLoginAs, setConfirmingLoginAs] = useState<boolean>(false)
  const [confirmingOnboard, setConfirmingOnboard] = useState<boolean>(false)
  const [isBusy, setIsBusy] = useState<boolean>(false)

  const dashboardUrl = process.env.REACT_APP_DASHBOARD_URL

  const isMasterContractor = customer.isMasterContractor
  const isOnboarded = isMasterContractor && isCustomerOnboardedAsMasterContractor(customer)
  const needsOnboarding = isMasterContractor && !isOnboarded
  const showCompleteOnboarding = needsOnboarding && canOnboardMasterContractor
  // With neither action permitted the action stack is not rendered at all, so no empty margin
  // sits under the last row.
  const hasActions = showCompleteOnboarding || canLoginAs

  const handleCopyId = useCallback(async () => {
    if (!navigator.clipboard) {
      snackbar?.enqueueSnackbar('Unable to copy customer ID', { variant: 'error' })

      return
    }

    try {
      await navigator.clipboard.writeText(customer.id)
      snackbar?.enqueueSnackbar('Customer ID copied', { variant: 'success' })
    } catch {
      snackbar?.enqueueSnackbar('Unable to copy customer ID', { variant: 'error' })
    }
  }, [customer.id, snackbar])

  const handleOpenLoginAs = useCallback(() => {
    setConfirmingLoginAs(true)
  }, [])

  const handleCloseLoginAs = useCallback(() => {
    setConfirmingLoginAs(false)
  }, [])

  const handleOpenOnboard = useCallback(() => {
    setConfirmingOnboard(true)
  }, [])

  const handleCloseOnboard = useCallback(() => {
    setConfirmingOnboard(false)
  }, [])

  const handleLoginAs = useCallback(async () => {
    if (isBusy || !dashboardUrl) {
      return
    }

    setIsBusy(true)

    try {
      const tokens = await asyncDispatchWithErrorToast<LoginAsCustomerResult>(
        AdminLoginAsCustomer.request({ partnerId: customer.id }),
      )

      if (!tokens) {
        return
      }

      const target = buildSignInAsUrl(dashboardUrl, tokens)

      window.open(target, '_blank', 'noopener,noreferrer')
      setConfirmingLoginAs(false)
    } finally {
      setIsBusy(false)
    }
  }, [customer.id, dashboardUrl, asyncDispatchWithErrorToast, isBusy])

  const handleCompleteOnboarding = useCallback(async () => {
    if (isBusy) {
      return
    }

    setIsBusy(true)

    try {
      const result = await asyncDispatchWithErrorToast(
        AdminOnboardMasterContractor.request({ partnerId: customer.id }),
      )

      if (result === null) {
        return
      }

      snackbar?.enqueueSnackbar('Master contractor onboarded', { variant: 'success' })
      setConfirmingOnboard(false)
      onCustomerChanged()
    } finally {
      setIsBusy(false)
    }
  }, [customer.id, asyncDispatchWithErrorToast, snackbar, onCustomerChanged, isBusy])

  return (
    <Fragment>
      <div
        data-testid="admin-customer-details-card"
        css={tw`flex w-full max-w-[520px] flex-col rounded-2xl bg-shades-0 p-6`}
      >
        <div
          css={tw`flex flex-row items-start justify-between gap-4 border-0 border-b border-solid border-shades-100 pb-4`}
        >
          <Typography variant="h2" css={tw`min-w-0 break-words`}>
            {resolveCustomerDisplayName(customer)}
          </Typography>
          <CustomerLogoUploader
            customer={customer}
            isReadOnly={!canUploadLogo}
            onCustomerChanged={onCustomerChanged}
          />
        </div>
        <DataRow
          title="Company ID"
          direction="column"
          text={customer.id}
          textClassName={tw`break-all`}
          action={
            <Tooltip title="Copy">
              <span>
                <Button
                  size="small"
                  color="grey"
                  iconOnly
                  leftIcon={
                    <GSIcon
                      iconName="gs_square_behind_square_1"
                      size={20}
                      css={tw`text-shades-1000`}
                    />
                  }
                  aria-label="Copy company ID"
                  onClick={handleCopyId}
                  data-testid="admin-customer-copy-id-button"
                />
              </span>
            </Tooltip>
          }
        />
        <DataRow
          title="Customer type"
          component={
            isMasterContractor ? (
              <Chip
                text="Master Contractor"
                className={tw`bg-primary-100`}
                textClassName={tw`text-primary-700`}
              />
            ) : (
              <Chip
                text="Standard"
                className={tw`bg-shades-100`}
                textClassName={tw`text-shades-500`}
              />
            )
          }
        />
        {isMasterContractor ? (
          <DataRow
            title="Onboarding"
            component={
              isOnboarded ? (
                <Chip
                  text="Onboarded"
                  className={{ backgroundColor: semanticColors.feedback.successBg }}
                  textClassName={{ color: semanticColors.feedback.successFg }}
                />
              ) : (
                <Chip
                  text="Onboarding incomplete"
                  className={{ backgroundColor: semanticColors.feedback.warningBg }}
                  textClassName={{ color: semanticColors.feedback.warningFg }}
                />
              )
            }
          />
        ) : null}
        {/* The last row's hairline is the rule above the actions, as in the dashboard panel. */}
        {hasActions ? (
          <div css={tw`mt-4 flex flex-col gap-2`}>
            {showCompleteOnboarding ? (
              <Button
                size="small"
                color="grey"
                fullWidth
                leftIcon={
                  <GSIcon iconName="gs_circle_check" size={20} css={tw`text-shades-1000`} />
                }
                onClick={handleOpenOnboard}
                data-testid="admin-customer-complete-onboarding-button"
              >
                Complete onboarding
              </Button>
            ) : null}
            {canLoginAs ? (
              <Tooltip title={dashboardUrl ? '' : 'REACT_APP_DASHBOARD_URL is not configured'}>
                <span css={tw`flex`}>
                  <Button
                    size="small"
                    color="grey"
                    fullWidth
                    leftIcon={
                      <GSIcon
                        iconName="gs_square_arrow_top_right"
                        size={20}
                        css={tw`text-shades-1000`}
                      />
                    }
                    disabled={!dashboardUrl}
                    onClick={handleOpenLoginAs}
                    data-testid="admin-customer-login-as-button"
                  >
                    Log in as customer
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>
      <CommonModal
        isOpen={confirmingLoginAs}
        title="Open the customer’s dashboard?"
        text={`This opens ${resolveCustomerDisplayName(
          customer,
        )}’s live dashboard in a new tab, signed in as them. Actions you take there affect the real account.`}
        onClose={handleCloseLoginAs}
        buttons={
          <Fragment>
            <Button size="small" color="grey" onClick={handleCloseLoginAs}>
              Cancel
            </Button>
            <Button size="small" color="dark" loading={isBusy} onClick={handleLoginAs}>
              Open customer dashboard
            </Button>
          </Fragment>
        }
      />
      <CommonModal
        isOpen={confirmingOnboard}
        title="Complete master-contractor onboarding?"
        text={`This marks ${resolveCustomerDisplayName(customer)} as fully onboarded.`}
        onClose={handleCloseOnboard}
        buttons={
          <Fragment>
            <Button size="small" color="grey" onClick={handleCloseOnboard}>
              Cancel
            </Button>
            <Button size="small" color="dark" loading={isBusy} onClick={handleCompleteOnboarding}>
              Complete onboarding
            </Button>
          </Fragment>
        }
      />
    </Fragment>
  )
}

export const CustomerDetailsCard = memo(CustomerDetailsCardComponent)
